import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isFeatureEnabled } from "@/lib/flags";
import { useSupabaseClient } from "@/lib/supabase";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";

async function fetchPlan(
  client: SupabaseClient<Database>,
  clerkId: string,
): Promise<string | null> {
  const { data, error } = await client
    .from("profiles")
    .select("plan, role")
    .eq("clerk_id", clerkId)
    .maybeSingle();
  if (error) return null;
  // Admins bypass plan check
  if (data?.role === "admin") return "beta";
  return data?.plan ?? null;
}

export function PlanGate({ children }: { children: ReactNode }) {
  const authEnabled = isFeatureEnabled("AUTH_ENABLED");
  const { user, isLoading: userLoading } = useCurrentUser();
  const { client: supabaseClient, isReady: isSupabaseReady } = useSupabaseClient();
  const [bridgeUnavailable, setBridgeUnavailable] = useState(false);

  useEffect(() => {
    if (!authEnabled || !user || isSupabaseReady) {
      setBridgeUnavailable(false);
      return;
    }

    // Token exchange retries its transient failures. Do not leave a signed-in
    // user on a blank page if that bridge remains unavailable afterwards.
    const timeout = window.setTimeout(() => setBridgeUnavailable(true), 8_000);
    return () => window.clearTimeout(timeout);
  }, [authEnabled, user, isSupabaseReady]);

  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ["profile-plan", user?.id],
    queryFn: () => fetchPlan(supabaseClient, user!.id),
    enabled: authEnabled && !!user && isSupabaseReady,
    staleTime: 60_000,
  });

  // Auth disabled → bypass gate
  if (!authEnabled) return <>{children}</>;

  // Still resolving user or plan
  if (userLoading) return null;

  // No user → ProtectedRoute handles redirect to /login
  if (!user) return <>{children}</>;

  if (!isSupabaseReady) {
    return bridgeUnavailable ? <Navigate to="/pending-access" replace /> : null;
  }

  if (planLoading) return null;

  // Pending or blocked → access wall
  if (plan !== "beta") {
    return <Navigate to="/pending-access" replace />;
  }

  return <>{children}</>;
}
